import { Button } from "@/ui/Button";
import { Block } from "@/ui/Field";
import { CalendarIcon, PickerField } from "@/ui/PickerField";
import { Screen } from "@/ui/Screen";
import { Display, Mono } from "@/ui/Text";

interface DateStepProps {
  readonly date: string | null;
  readonly onChange: (date: string | null) => void;
  readonly onContinue: () => void;
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** Today as an ISO date, the latest a birth can be. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Step 1 — birth date. Echoes the weekday back, which is a free correctness
 * check: people misremember the year far more often than the day, and
 * "Sunday" jogs it.
 */
export function DateStep({ date, onChange, onContinue }: DateStepProps) {
  const parsed = date ? new Date(`${date}T12:00:00`) : null;
  const valid = parsed !== null && !Number.isNaN(parsed.getTime());
  return (
    <Screen
      middle
      progress={1 / 3}
      foot={<Button label="Continue" disabled={!valid} onClick={onContinue} />}
    >
      <Display>When were you born?</Display>
      <div className="mt-8">
        <PickerField
          label="Date of birth"
          type="date"
          name="birth-date"
          value={date ?? ""}
          prompt="Tap to choose your date"
          display={valid && parsed ? `${parsed.getDate()} ${MONTHS[parsed.getMonth()]} ${parsed.getFullYear()}` : undefined}
          icon={<CalendarIcon />}
          min="1900-01-01"
          max={todayIso()}
          required
          onChange={(e) => onChange(e.target.value || null)}
        />
      </div>
      {valid && parsed && (
        <div className="mt-6 text-center">
          <Block>
            <Mono className="text-mid">
              {MONTHS[parsed.getMonth()]!.slice(0, 3).toUpperCase()}&nbsp;&nbsp;{parsed.getFullYear()}
            </Mono>
            <p className="font-display mt-2 text-[34px] leading-none">{parsed.getDate()}</p>
            <p className="mt-1 text-[13px] text-mid">{WEEKDAYS[parsed.getDay()]}</p>
          </Block>
        </div>
      )}
    </Screen>
  );
}
